/**
 * Fetch wrappers for the bridge's HTTP API. All endpoints are same-origin
 * so we use relative URLs and let the browser handle cookies/credentials
 * if any are added later. Currently the bridge uses bearer-style refresh
 * tokens in JSON bodies, no cookies.
 */

import { baseHttpOrigin } from './auth.ts'

export interface BridgeServer {
  readonly id: string
  readonly label: string
}

export interface ListServersResponse {
  readonly servers: ReadonlyArray<BridgeServer>
}

export interface LinkStartResponse {
  readonly linkSessionId: string
  readonly expiresAt: string
}

export interface LinkRedeemResponse {
  readonly refreshToken: string
  readonly linkId: string
  readonly agid: string
}

export interface SessionTokenResponse {
  readonly sessionToken: string
  readonly expiresAt: string
  readonly debugAccess?: boolean
}

class ApiError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(baseHttpOrigin() + path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const text = await res.text()
  let parsed: unknown = null
  try { parsed = text.length > 0 ? JSON.parse(text) : null } catch { /* fall through */ }
  if (!res.ok) {
    const code = (parsed && typeof parsed === 'object' && 'error' in parsed && typeof (parsed as Record<string, unknown>).error === 'string')
      ? (parsed as Record<string, string>).error
      : `http_${res.status}`
    throw new ApiError(`${path} failed: ${code}`, code, res.status)
  }
  return (parsed ?? {}) as T
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(baseHttpOrigin() + path)
  if (!res.ok) throw new ApiError(`${path} failed`, `http_${res.status}`, res.status)
  return (await res.json()) as T
}

export async function fetchServers(): Promise<ListServersResponse> {
  return getJson<ListServersResponse>('/api/servers')
}

export async function linkStart(username: string, serverId: string): Promise<LinkStartResponse> {
  return postJson<LinkStartResponse>('/api/link/start', { username, serverId })
}

export async function linkRedeem(linkSessionId: string, code: string): Promise<LinkRedeemResponse> {
  return postJson<LinkRedeemResponse>('/api/link/redeem', { linkSessionId, code })
}

export async function refreshSession(refreshToken: string): Promise<SessionTokenResponse> {
  return postJson<SessionTokenResponse>('/api/session/refresh', { refreshToken })
}

export type BugReportCategory = 'audio' | 'linking' | 'ui' | 'crash' | 'other'

export interface BugReportRequest {
  readonly refreshToken: string
  readonly category: BugReportCategory
  readonly summary: string
  readonly details: string
  readonly contact?: string
  readonly clientVersion?: string
  readonly clientOs?: string
}

export interface BugReportResponse {
  readonly ok: true
  readonly id: number
}

export async function submitBugReport(body: BugReportRequest): Promise<BugReportResponse> {
  return postJson<BugReportResponse>('/api/bug-reports', body)
}

export interface HealthResponse {
  readonly ok: boolean
  readonly region: string
  readonly uptimeSeconds: number
  readonly rcon: Record<string, string>
}

export async function fetchHealth(): Promise<HealthResponse> {
  return getJson<HealthResponse>('/api/health')
}

export { ApiError }
